export interface DiffPartT {
    text: string;
    kind: "same" | "removed" | "added";
}

/** Splits into words and whitespace so a diff keeps the original spacing. */
function tokens(text: string): string[] {
    return text.match(/\s+|[^\s]+/g) ?? [];
}

/**
 * Word-level diff of the English source against a translation, so a reviewer sees what changed
 * ("past" -> "last") instead of comparing two paragraphs by eye.
 */
export function diffWords(source: string, translation: string): DiffPartT[] {
    const a = tokens(source);
    const b = tokens(translation);
    const lcs: number[][] = Array.from({ length: a.length + 1 }, () =>
        Array.from<number>({ length: b.length + 1 }).fill(0)
    );
    const at = (r: number, c: number) => lcs[r]?.[c] ?? 0;
    for (let i = a.length - 1; i >= 0; i--) {
        for (let j = b.length - 1; j >= 0; j--) {
            lcs[i]![j] = a[i] === b[j] ? at(i + 1, j + 1) + 1 : Math.max(at(i + 1, j), at(i, j + 1));
        }
    }

    const parts: DiffPartT[] = [];
    const push = (text: string, kind: DiffPartT["kind"]) => {
        const last = parts[parts.length - 1];
        if (last?.kind === kind) last.text += text;
        else parts.push({ text, kind });
    };
    let i = 0;
    let j = 0;
    while (i < a.length && j < b.length) {
        if (a[i] === b[j]) {
            push(a[i] ?? "", "same");
            i++;
            j++;
        } else if (at(i + 1, j) >= at(i, j + 1)) {
            push(a[i++] ?? "", "removed");
        } else {
            push(b[j++] ?? "", "added");
        }
    }
    while (i < a.length) push(a[i++] ?? "", "removed");
    while (j < b.length) push(b[j++] ?? "", "added");
    return parts;
}
